'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Home, Search, ArrowLeft, Phone } from 'lucide-react';
import { logPage404 } from '@/lib/notFoundLogger.client';

// Popular destinations so a visitor who lands here can immediately get where
// they meant to go — a 404 should be a redirect-yourself hub, not a dead end.
const POPULAR = [
  { label: 'Shop All Products', href: '/products' },
  { label: 'Track Your Order', href: '/track-order' },
  { label: 'Shipping', href: '/shipping' },
  { label: 'Returns', href: '/returns' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Help Center', href: '/help' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact Us', href: '/contact' },
];

export default function NotFound() {
  const router = useRouter();
  const [q, setQ] = useState('');

  // Log the 404 — the client logger now only reports actionable (internal
  // broken-link) 404s, so crawler/external hits don't clutter the bug board.
  useEffect(() => {
    logPage404(window.location.pathname + window.location.search);
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    router.push(term ? `/products?search=${encodeURIComponent(term)}` : '/products');
  };

  return (
    <div className="min-h-screen bg-sb flex items-center justify-center px-4 py-16">
      <div className="max-w-2xl w-full text-center">
        <div className="mb-6">
          <h1 className="text-8xl md:text-9xl font-bold text-gray-200">404</h1>
          <p className="text-2xl font-semibold text-gray-800 -mt-6 md:-mt-8">Page Not Found</p>
        </div>

        <p className="text-gray-600 mb-8 max-w-lg mx-auto">
          Sorry, we couldn&apos;t find that page — it may have moved or no longer exists.
          Try searching, or pick one of the popular pages below.
        </p>

        {/* Search — sends visitors to the product catalog */}
        <form onSubmit={onSearch} className="flex max-w-md mx-auto mb-10">
          <input maxLength={80}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search blinds, shades, fabrics…"
            className="flex-1 border border-gray-300 rounded-l-lg px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-sb-gold"
            aria-label="Search products"
          />
          <button
            type="submit"
            className="btn-sb-primary rounded-l-none inline-flex items-center px-5"
          >
            <Search className="w-5 h-5" />
          </button>
        </form>

        {/* Popular destinations */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto mb-10 text-left">
          {POPULAR.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="flex items-center px-4 py-3 rounded-lg bg-white border border-gray-200 hover:border-sb-gold hover:shadow-sm transition-colors text-gray-800 font-medium"
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link href="/" className="btn-sb-primary inline-flex items-center justify-center shadow-lg">
            <Home className="w-5 h-5 mr-2" />
            Go Home
          </Link>
          <Link href="/contact" className="btn-sb-secondary inline-flex items-center justify-center shadow-lg">
            <Phone className="w-5 h-5 mr-2" />
            Talk to an Expert
          </Link>
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}
